import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Form,
  Select,
  DatePicker,
  Input,
  Button,
  Table,
  InputNumber,
  Space,
  Typography,
  Card,
  Row,
  Col,
  Divider,
  Popconfirm,
  message,
  Statistic,
  Modal,
  Tag,
} from 'antd';
import { PlusOutlined, DeleteOutlined, SaveOutlined, SendOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { createMR, updateMR, submitMR, getMR, getLookups, getCostCenterConfig, costCenterLabel } from '../../api';
import type { CatalogSearchResult, CostCenterRef } from '../../api';
import { useAuth } from '../../context/AuthContext';
import PartNumberCombobox from '../../components/PartNumberCombobox';
import CategoryTreeSelect from '../../components/CategoryTreeSelect';
import CostCenterSelect from '../../components/CostCenterSelect';
import MRCreateRequester from './MRCreateRequester';

const { Title } = Typography;
const { TextArea } = Input;

interface LineItem {
  key: string;
  catalogItemId?: number | null;
  allowedUnitCategories?: string | null;
  partNumber: string;
  description: string;
  categoryId?: number;
  vendorId?: number;
  vendorOther?: string;
  quantity: number;
  unitId?: number;
  unitPrice: number;
  currencyId?: number;
  notes: string;
  costCenterId?: number | null;
  costCenter?: CostCenterRef | null;
}

const OTHER_VENDOR_ID = -999;

interface Lookups {
  departments: Array<{ id: number; name: string }>;
  vendors: Array<{ id: number; name: string }>;
  categories: Array<{ id: number; name: string }>;
  categoryTree: Array<any>;
  units: Array<{ id: number; name: string; abbreviation?: string; category?: string | null }>;
  currencies: Array<{ id: number; code: string; name: string }>;
}

let lineKeyCounter = 0;
const nextKey = () => `line_${++lineKeyCounter}`;

// Full grid: procurement, approvers, admins. Requesters and account managers get the
// catalog-first screen (MRCreateRequester, 2026-09-06 redesign).
const MRCreateFull: React.FC = () => {
  const navigate = useNavigate();
  const { mrId: editMrId } = useParams<{ mrId?: string }>();
  const isEdit = Boolean(editMrId);
  const { user, canAccess, myDepartmentIds } = useAuth();
  // Requesters may only raise MRs for their home department plus any extra departments assigned in Admin.
  const canPickAnyDepartment = canAccess('purchasing.mrs', 'full');
  const [form] = Form.useForm();
  const [lookups, setLookups] = useState<Lookups>({
    departments: [],
    vendors: [],
    categories: [],
    categoryTree: [],
    units: [],
    currencies: [],
  });
  const [lines, setLines] = useState<LineItem[]>([
    {
      key: nextKey(),
      partNumber: '',
      description: '',
      quantity: 1,
      unitPrice: 0,
      notes: '',
    },
  ]);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Cost allocation: header default applied to every line; each line can override.
  const [defaultCostCenterId, setDefaultCostCenterId] = useState<number | null>(null);
  const [defaultCostCenter, setDefaultCostCenter] = useState<CostCenterRef | null>(null);
  const [ccRequired, setCcRequired] = useState(false);
  useEffect(() => {
    getCostCenterConfig().then((c) => setCcRequired(!!c.requiredNow)).catch(() => {});
  }, []);
  const applyDefaultCostCenter = (next: number | null, cc: CostCenterRef | null) => {
    const prev = defaultCostCenterId;
    setDefaultCostCenterId(next);
    setDefaultCostCenter(cc);
    // Lines that were empty or still carried the previous default follow the new default; explicit overrides stay.
    setLines((ls) => ls.map((l) => (!l.costCenterId || l.costCenterId === prev ? { ...l, costCenterId: next, costCenter: cc } : l)));
  };
  const allocationSummary = () => {
    const groups = new Map<string, number>();
    for (const l of lines) {
      const label = l.costCenterId ? (l.costCenter ? costCenterLabel(l.costCenter) : `#${l.costCenterId}`) : 'No cost center';
      groups.set(label, (groups.get(label) || 0) + 1);
    }
    return Array.from(groups.entries());
  };

  useEffect(() => {
    const loadLookups = async () => {
      try {
        const data = await getLookups();
        setLookups(data);
        if (user?.departmentId) {
          form.setFieldValue('departmentId', user.departmentId);
        }
        if (data.currencies?.length > 0) {
          const defaultCurrency = data.currencies.find((c: any) => c.code === 'SAR') || data.currencies[0];
          setLines((prev) =>
            prev.map((l) => ({ ...l, currencyId: defaultCurrency.id }))
          );
        }
      } catch (err) {
        message.error('Failed to load form data');
      }
    };
    loadLookups();
  }, [form, user]);

  // Load existing MR for edit mode
  useEffect(() => {
    if (!isEdit || !editMrId) return;
    const loadMR = async () => {
      try {
        const mr = await getMR(editMrId);
        if (mr.status !== 'Draft') {
          message.error('Only Draft MRs can be edited');
          navigate(`/mrs/${editMrId}`);
          return;
        }
        form.setFieldsValue({
          departmentId: mr.departmentId,
          neededBy: mr.neededBy ? dayjs(mr.neededBy) : undefined,
          urgency: mr.urgency,
          notes: mr.notes || '',
        });
        setDefaultCostCenterId(mr.defaultCostCenterId || null);
        setDefaultCostCenter(mr.defaultCostCenter || null);
        if (mr.lines && mr.lines.length > 0) {
          setLines(mr.lines.map((l: any) => ({
            key: nextKey(),
            partNumber: l.partNumber || '',
            description: l.itemDescription || '',
            categoryId: l.categoryId || undefined,
            vendorId: l.vendorId || undefined,
            quantity: Number(l.qty) || 1,
            unitId: l.unitId || undefined,
            unitPrice: Number(l.unitPrice) || 0,
            currencyId: l.currencyId || undefined,
            notes: l.lineNotes || '',
            costCenterId: l.costCenterId || null,
            costCenter: l.costCenter || null,
          })));
        }
      } catch (err: any) {
        message.error('Failed to load MR for editing');
        navigate('/mrs');
      }
    };
    loadMR();
  }, [isEdit, editMrId]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateLine = (key: string, field: keyof LineItem, value: any) => {
    setLines((prev) =>
      prev.map((line) => (line.key === key ? { ...line, [field]: value } : line))
    );
  };

  const addLine = () => {
    const defaultCurrency = lookups.currencies?.find((c) => c.code === 'SAR') || lookups.currencies?.[0];
    setLines((prev) => [
      ...prev,
      {
        key: nextKey(),
        partNumber: '',
        description: '',
        quantity: 1,
        unitPrice: 0,
        currencyId: defaultCurrency?.id,
        notes: '',
        costCenterId: defaultCostCenterId,
        costCenter: defaultCostCenter,
      },
    ]);
  };

  const removeLine = (key: string) => {
    if (lines.length <= 1) {
      message.warning('At least one line is required');
      return;
    }
    setLines((prev) => prev.filter((l) => l.key !== key));
  };

  const getLineTotal = (line: LineItem) => {
    return (line.quantity || 0) * (line.unitPrice || 0);
  };

  const grandTotal = lines.reduce((sum, l) => sum + getLineTotal(l), 0);

  const handleSave = async (shouldSubmit: boolean) => {
    try {
      const values = await form.validateFields();
      const invalidLines = lines.filter((l) => !l.description || l.quantity <= 0);
      if (invalidLines.length > 0) {
        message.error('Please fill in description and quantity for all lines');
        return;
      }

      const linesWithoutUnit = lines.filter((l) => !l.unitId);
      if (linesWithoutUnit.length > 0) {
        message.error('Please select a unit for all lines');
        return;
      }

      if (shouldSubmit) {
        const missing = lines.filter((l) => !l.costCenterId).length;
        if (ccRequired && missing > 0) {
          message.error(`Cost center is required on every line before submitting (${missing} line${missing === 1 ? '' : 's'} missing)`);
          return;
        }
        const summary = allocationSummary();
        const confirmed = await new Promise<boolean>((resolve) => {
          const dialog = Modal.confirm({
            title: 'Confirm cost allocation',
            okText: 'Submit',
            cancelText: 'Back',
            content: (
              <div>
                <p style={{ marginBottom: 8 }}>This request will be charged to:</p>
                <ul style={{ paddingLeft: 18, margin: 0 }}>
                  {summary.map(([label, n]) => (
                    <li key={label} style={{ color: label === 'No cost center' ? '#cf1322' : undefined }}>
                      {n} line{n === 1 ? '' : 's'} → <strong>{label}</strong>
                    </li>
                  ))}
                </ul>
              </div>
            ),
            onOk: () => { dialog.destroy(); resolve(true); },
            onCancel: () => { dialog.destroy(); resolve(false); },
          });
        });
        if (!confirmed) return;
        setSubmitting(true);
      } else {
        setSaving(true);
      }

      const mrData = {
        departmentId: values.departmentId,
        neededBy: values.neededBy.format('YYYY-MM-DD'),
        urgency: values.urgency,
        notes: values.notes || '',
        defaultCostCenterId: defaultCostCenterId || null,
        lines: lines.map((l) => {
          const vendorId = l.vendorId === OTHER_VENDOR_ID ? undefined : (l.vendorId || undefined);
          const vendorNote = l.vendorId === OTHER_VENDOR_ID && l.vendorOther
            ? `[Other Vendor: ${l.vendorOther}]`
            : '';
          const lineNotes = [vendorNote, l.notes].filter(Boolean).join(' ') || undefined;
          return {
            catalogItemId: l.catalogItemId || undefined,
            partNumber: l.partNumber || undefined,
            itemDescription: l.description,
            categoryId: l.categoryId || undefined,
            vendorId,
            vendorName: l.vendorId === OTHER_VENDOR_ID ? l.vendorOther : undefined,
            qty: l.quantity || 1,
            unitId: l.unitId || undefined,
            unitPrice: l.unitPrice || 0,
            currencyId: l.currencyId || undefined,
            lineNotes,
            costCenterId: l.costCenterId || null,
          };
        }),
      };

      let mrId: string | number;
      if (isEdit && editMrId) {
        const updated = await updateMR(editMrId, mrData);
        mrId = updated.id || updated.mrId;
        if (shouldSubmit && mrId) {
          await submitMR(mrId as number);
          message.success('MR updated and submitted successfully');
        } else {
          message.success('MR updated successfully');
        }
      } else {
        const created = await createMR(mrData);
        mrId = created.id || created.mrId;
        if (shouldSubmit && mrId) {
          await submitMR(mrId as number);
          message.success('MR created and submitted successfully');
        } else {
          message.success('MR saved as draft');
        }
      }

      navigate(`/mrs/${mrId}`, { replace: true }); // the form drops out of history so Back returns to where you started
    } catch (err: any) {
      const msg = err.response?.data?.error || err.message || 'Failed to save MR';
      message.error(msg);
    } finally {
      setSaving(false);
      setSubmitting(false);
    }
  };

  const lineColumns = [
    {
      title: '#',
      width: 50,
      render: (_: any, __: any, index: number) => index + 1,
    },
    {
      title: 'Part Number',
      dataIndex: 'partNumber',
      width: 200,
      render: (_: any, record: LineItem) => (
        <PartNumberCombobox
          value={record.catalogItemId || null}
          allowFreeText
          freeTextValue={record.partNumber}
          onFreeTextChange={(text) => updateLine(record.key, 'partNumber', text)}
          onChange={(catalogItemId: number | null, item: CatalogSearchResult | null) => {
            if (item) {
              setLines((prev) =>
                prev.map((l) => {
                  if (l.key !== record.key) return l;
                  // Part B.1: if catalog item restricts unit categories and the
                  // default unit is not in the allowlist, drop it so the user picks
                  // a valid one.
                  const allowed = (item.allowedUnitCategories || '')
                    .split(',')
                    .map((s) => s.trim())
                    .filter(Boolean);
                  const candidateUnitId = item.unit?.id || l.unitId;
                  let nextUnitId = candidateUnitId;
                  if (allowed.length > 0 && candidateUnitId) {
                    const unit = lookups.units.find((u) => u.id === candidateUnitId);
                    if (unit && unit.category && !allowed.includes(unit.category)) {
                      nextUnitId = undefined;
                    }
                  }
                  return {
                    ...l,
                    catalogItemId: item.id,
                    allowedUnitCategories: item.allowedUnitCategories ?? null,
                    partNumber: item.partNumber,
                    description: item.description,
                    unitPrice: Number(item.defaultUnitPrice) || l.unitPrice,
                    unitId: nextUnitId,
                    categoryId: item.category?.id || l.categoryId,
                    vendorId: item.preferredVendor?.id || l.vendorId,
                    currencyId: item.currency?.id || l.currencyId,
                  };
                })
              );
            } else {
              setLines((prev) =>
                prev.map((l) =>
                  l.key === record.key
                    ? { ...l, catalogItemId: null, allowedUnitCategories: null }
                    : l
                )
              );
            }
          }}
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      width: 200,
      render: (_: any, record: LineItem) => (
        <Input
          size="small"
          value={record.description}
          onChange={(e) => updateLine(record.key, 'description', e.target.value)}
          placeholder="Description *"
          status={!record.description ? 'error' : undefined}
        />
      ),
    },
    {
      title: 'Category',
      dataIndex: 'categoryId',
      width: 160,
      render: (_: any, record: LineItem) => (
        <CategoryTreeSelect
          size="small"
          allowClear
          style={{ width: '100%' }}
          value={record.categoryId}
          onChange={(val) => updateLine(record.key, 'categoryId', val)}
          placeholder="Category"
          categoryTree={lookups.categoryTree}
          categories={lookups.categories}
        />
      ),
    },
    {
      title: 'Vendor',
      dataIndex: 'vendorId',
      width: 160,
      render: (_: any, record: LineItem) => (
        <div>
          <Select
            size="small"
            allowClear
            style={{ width: '100%' }}
            value={record.vendorId}
            onChange={(val) => {
              updateLine(record.key, 'vendorId', val);
              if (val !== OTHER_VENDOR_ID) {
                updateLine(record.key, 'vendorOther', '');
              }
            }}
            placeholder="Vendor"
            options={[
              ...lookups.vendors.map((v) => ({ label: v.name, value: v.id })),
              { label: '-- Other (new) --', value: OTHER_VENDOR_ID },
            ]}
            showSearch
            filterOption={(input, option) =>
              (option?.label as string || '').toLowerCase().includes(input.toLowerCase())
            }
          />
          {record.vendorId === OTHER_VENDOR_ID && (
            <Input
              size="small"
              style={{ marginTop: 4 }}
              value={record.vendorOther || ''}
              onChange={(e) => updateLine(record.key, 'vendorOther', e.target.value)}
              placeholder="Enter vendor name"
            />
          )}
        </div>
      ),
    },
    {
      title: 'Qty',
      dataIndex: 'quantity',
      width: 80,
      render: (_: any, record: LineItem) => (
        <InputNumber
          size="small"
          min={0.01}
          value={record.quantity}
          onChange={(val) => updateLine(record.key, 'quantity', val || 0)}
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: 'Unit',
      dataIndex: 'unitId',
      width: 140,
      render: (_: any, record: LineItem) => {
        // Part B.1: filter units to those whose category is in the catalog item's allowlist.
        const allowed = (record.allowedUnitCategories || '')
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean);
        const filteredUnits = allowed.length > 0
          ? lookups.units.filter((u) => u.category && allowed.includes(u.category))
          : lookups.units;
        const helper =
          allowed.length > 0
            ? `Allowed: ${allowed.join(', ')} (${filteredUnits.map((u) => u.abbreviation || u.name).join(', ') || 'none'})`
            : '';
        return (
          <div>
            <Select
              size="small"
              style={{ width: '100%' }}
              value={record.unitId}
              onChange={(val) => updateLine(record.key, 'unitId', val)}
              placeholder="Unit *"
              status={!record.unitId ? 'error' : undefined}
              options={filteredUnits.map((u) => ({
                label: u.abbreviation || u.name,
                value: u.id,
              }))}
            />
            {helper && (
              <div style={{ fontSize: 10, color: '#888', marginTop: 2, lineHeight: 1.2 }}>
                {helper}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: 'Unit Price',
      dataIndex: 'unitPrice',
      width: 110,
      render: (_: any, record: LineItem) => (
        <InputNumber
          size="small"
          min={0}
          step={0.01}
          value={record.unitPrice}
          onChange={(val) => updateLine(record.key, 'unitPrice', val || 0)}
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: 'Currency',
      dataIndex: 'currencyId',
      width: 100,
      render: (_: any, record: LineItem) => (
        <Select
          size="small"
          style={{ width: '100%' }}
          value={record.currencyId}
          onChange={(val) => updateLine(record.key, 'currencyId', val)}
          placeholder="Cur"
          options={lookups.currencies.map((c) => ({
            label: c.code,
            value: c.id,
          }))}
        />
      ),
    },
    {
      title: 'Total',
      width: 110,
      align: 'right' as const,
      render: (_: any, record: LineItem) =>
        getLineTotal(record).toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }),
    },
    {
      title: 'Cost Center',
      dataIndex: 'costCenterId',
      width: 210,
      render: (_: any, record: LineItem) => (
        <CostCenterSelect
          size="small"
          value={record.costCenterId ?? null}
          current={record.costCenter || null}
          status={ccRequired && !record.costCenterId ? 'error' : undefined}
          placeholder={ccRequired ? 'Cost center *' : 'Cost center'}
          onChange={(id, cc) => setLines((prev) => prev.map((l) => (l.key === record.key ? { ...l, costCenterId: id, costCenter: cc } : l)))}
        />
      ),
    },
    {
      title: 'Notes',
      dataIndex: 'notes',
      width: 120,
      render: (_: any, record: LineItem) => (
        <Input
          size="small"
          value={record.notes}
          onChange={(e) => updateLine(record.key, 'notes', e.target.value)}
          placeholder="Notes"
        />
      ),
    },
    {
      title: '',
      width: 50,
      render: (_: any, record: LineItem) => (
        <Popconfirm
          title="Delete this line?"
          onConfirm={() => removeLine(record.key)}
          okText="Yes"
          cancelText="No"
        >
          <Button
            type="text"
            danger
            size="small"
            icon={<DeleteOutlined />}
          />
        </Popconfirm>
      ),
    },
  ];

  return (
    <div>
      <Title level={4}>{isEdit ? 'Edit Material Request' : 'Create Material Request'}</Title>

      <Card style={{ marginBottom: 16 }}>
        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col xs={24} sm={12} md={6}>
              <Form.Item
                name="departmentId"
                label="Department"
                rules={[{ required: true, message: 'Select department' }]}
              >
                <Select
                  placeholder="Select Department"
                  options={lookups.departments
                    .filter((d) => canPickAnyDepartment || myDepartmentIds.includes(d.id))
                    .map((d) => ({
                      label: d.name,
                      value: d.id,
                    }))}
                  showSearch
                  filterOption={(input, option) =>
                    (option?.label as string || '').toLowerCase().includes(input.toLowerCase())
                  }
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Form.Item
                name="neededBy"
                label="Needed By"
                rules={[{ required: true, message: 'Select date' }]}
              >
                <DatePicker
                  style={{ width: '100%' }}
                  disabledDate={(current) => current && current < dayjs().startOf('day')}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Form.Item
                name="urgency"
                label="Urgency"
                rules={[{ required: true, message: 'Select urgency' }]}
                initialValue="Normal"
              >
                <Select
                  options={[
                    { label: 'Low', value: 'Low' },
                    { label: 'Normal', value: 'Normal' },
                    { label: 'High', value: 'High' },
                    { label: 'Critical', value: 'Critical' },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Form.Item
                label={<span>Allocate to {ccRequired ? <Tag color="red" style={{ marginLeft: 6 }}>required</Tag> : <Tag style={{ marginLeft: 6 }}>optional</Tag>}</span>}
                required={ccRequired}
                tooltip="Project, collection or client › sub this request belongs to. Applied to every line; change a single line in the table below."
              >
                <CostCenterSelect
                  value={defaultCostCenterId}
                  onChange={applyDefaultCostCenter}
                  placeholder="Project / collection / client › sub"
                  status={ccRequired && !defaultCostCenterId && lines.some((l) => !l.costCenterId) ? 'error' : undefined}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Form.Item name="notes" label="Notes">
                <TextArea rows={1} placeholder="Optional notes" />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Card>

      <Card
        title="Line Items"
        extra={
          <Button type="dashed" icon={<PlusOutlined />} onClick={addLine}>
            Add Line
          </Button>
        }
      >
        <Table
          columns={lineColumns}
          dataSource={lines}
          rowKey="key"
          pagination={false}
          size="small"
          scroll={{ x: 1600 }}
        />

        <Divider />

        <Row justify="end" style={{ marginBottom: 16 }}>
          <Col>
            <Statistic
              title="Grand Total"
              value={grandTotal}
              precision={2}
              style={{ textAlign: 'right' }}
            />
          </Col>
        </Row>

        <Row justify="end">
          <Space>
            <Button onClick={() => navigate('/mrs')}>Cancel</Button>
            <Button
              icon={<SaveOutlined />}
              loading={saving}
              onClick={() => handleSave(false)}
            >
              Save as Draft
            </Button>
            <Button
              type="primary"
              icon={<SendOutlined />}
              loading={submitting}
              onClick={() => handleSave(true)}
            >
              Save & Submit
            </Button>
          </Space>
        </Row>
      </Card>
    </div>
  );
};

// Phase 2 (2026-09-06): every role uses the catalog-first screen; MRCreateRequester adds the
// procurement columns for procurement / approver / admin. MRCreateFull (the old grid) is kept
// below for reference only and is no longer routed.
const MRCreate: React.FC = () => <MRCreateRequester />;
void MRCreateFull;

export default MRCreate;
